import { action, makeObservable, observable } from 'mobx';
import { notifyActionFinished } from '~/components/actions/ActionNotice';
import { DomainAction } from '~/lib/ApiModel';
import { hasTerminated } from '~/lib/helper/ActionHelper';
import { delay, withTimeout } from '~/lib/helper/PromiseHelper';
import { ISiteServicesApiClient } from '~/lib/SiteServicesApiClient';
import { LoadingTracker, LoadStatus } from '~/lib/store/LoadingTracker';

export const POLL_INTERVAL_MS = 1500;
/** How long an action is polled at `POLL_INTERVAL_MS`; a renewal normally completes well inside it. */
export const SLOW_POLL_AFTER_MS = 5 * 60 * 1000;
/** The interval for an action still pending after `SLOW_POLL_AFTER_MS`, such as an order held for review. */
export const SLOW_POLL_INTERVAL_MS = 30000;
const REQUEST_TIMEOUT_MS = 10000;

// A payment-blocked action stays incomplete until its invoice is paid, so it is not polled.
function isInProgress(domainAction: DomainAction): boolean {
    const { action } = domainAction;
    return !!action && !hasTerminated(action) && !action.blocking_invoice_id;
}

/** Polls one domain action until it finishes, is blocked on payment, or is stopped. */
export class DomainActionWatcher {
    /** The action as last fetched; undefined until a fetch succeeds. Deep, as `ActionMonitor` requires. */
    @observable
    domainAction: DomainAction | undefined = undefined;

    /** Tracks the first fetch, whose failure stays shown until a later poll succeeds. */
    readonly loadingTracker = new LoadingTracker();

    private polling: AbortController | undefined = undefined;

    /**
     * `onFinished` runs once the action completes, errors or disappears, but not when it is blocked
     * on payment. A rejection is retried on the next poll until it succeeds or the watch stops.
     */
    constructor(
        readonly actionId: number,
        private readonly siteApiClient: ISiteServicesApiClient,
        private readonly onFinished?: (domainAction: DomainAction) => Promise<void>
    ) {
        makeObservable(this);
    }

    /** Fetches the action and polls it while in progress, abandoning any earlier watch. */
    start = (): void => {
        this.stop();
        const polling = new AbortController();
        this.polling = polling;
        void this.watch(polling.signal);
    };

    stop(): void {
        this.polling?.abort();
        this.polling = undefined;
    }

    // Each poll waits for the previous response, so one slower than the interval is still acted on.
    private async watch(signal: AbortSignal): Promise<void> {
        const started = Date.now();
        let domainAction: DomainAction | undefined;
        try {
            domainAction = await this.loadingTracker.track(() => this.fetch(signal));
            this.setDomainAction(domainAction);
        } catch {
            if (signal.aborted) return;
        }

        while (domainAction === undefined || isInProgress(domainAction)) {
            let polled: DomainAction;
            try {
                await this.waitForNextPoll(started, signal);
                if (document.hidden) continue;
                polled = await this.fetch(signal);
            } catch {
                if (signal.aborted) return;
                continue;
            }
            this.setDomainAction(polled);
            if (domainAction === undefined) {
                this.loadingTracker.setStatus(LoadStatus.Complete);
            } else if (polled.action && hasTerminated(polled.action)) {
                notifyActionFinished(polled.action);
            }
            domainAction = polled;
        }

        if (domainAction.action && !hasTerminated(domainAction.action)) return;
        while (!(await this.tryFinish(domainAction))) {
            try {
                await this.waitForNextPoll(started, signal);
            } catch {
                return;
            }
        }
    }

    // Ends early when the tab becomes visible, so a returning customer does not wait out a slow-poll interval.
    private async waitForNextPoll(started: number, signal: AbortSignal): Promise<void> {
        signal.throwIfAborted();
        const pendingFor = Date.now() - started;
        const wait = new AbortController();
        const onAbort = () => wait.abort();
        const onVisibilityChange = () => {
            if (!document.hidden) wait.abort();
        };
        signal.addEventListener('abort', onAbort, { once: true });
        document.addEventListener('visibilitychange', onVisibilityChange);
        try {
            await delay(pendingFor < SLOW_POLL_AFTER_MS ? POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS, wait.signal);
        } catch {
            signal.throwIfAborted();
        } finally {
            signal.removeEventListener('abort', onAbort);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        }
    }

    private async tryFinish(domainAction: DomainAction): Promise<boolean> {
        try {
            await this.onFinished?.(domainAction);
            return true;
        } catch {
            return false;
        }
    }

    // Throws once stopped, so a response that ignored the abort is not acted on.
    private async fetch(signal: AbortSignal): Promise<DomainAction> {
        const domainAction = await withTimeout(
            (requestSignal) => this.siteApiClient.getDomainAction(this.actionId, requestSignal),
            REQUEST_TIMEOUT_MS,
            signal
        );
        signal.throwIfAborted();
        return domainAction;
    }

    @action
    private setDomainAction(domainAction: DomainAction) {
        this.domainAction = domainAction;
    }
}
